import AccountLoginForm from '@components/account/account-login/account-login-form';
import { v2_fetchAuthorizedCustomer } from '@crocodille/common';
import { assert, validateRedirectUrl } from '@crocodille/util';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { useRouter } from '@framework/router';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import * as routing from '@routing';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { getProviders } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import type { GetServerSideProps } from 'next/types';

const ALLOWED_REDIRECT_ORIGINS = [
  'https://bbdomu.cz',
  'https://sk.bbdomu.cz',
  'https://bbnew-staging.zensys.cz',
] as const;

export interface LoginPageProps {
  readonly providers: readonly string[];
}

const LoginPage: AppPage<LoginPageProps> = ({
  providers: enabledProviders,
}) => {
  const { query } = useRouter();
  const { t } = useTranslation();

  assert(!query.callbackUrl || typeof query.callbackUrl === 'string');

  const error = query.error;

  assert(!error || typeof error === 'string');

  const validatedCallbackUrl = validateRedirectUrl(
    query.callbackUrl,
    ALLOWED_REDIRECT_ORIGINS,
  );

  return (
    <>
      <PageMeta pageTitle={t('login_page.title')} description="" />
      <AccountLoginForm
        enabledProviders={enabledProviders}
        error={error}
        callbackUrl={validatedCallbackUrl ?? undefined}
      />
    </>
  );
};

LoginPage.getLayout = (page) => {
  return getAppLayoutWithCart(page);
};

export const getServerSideProps: GetServerSideProps<LoginPageProps> = async (
  ctx,
) => {
  const app = getApp(ctx);

  const callbackUrl = ctx.query.callbackUrl;

  assert(!callbackUrl || typeof callbackUrl === 'string');

  const validatedCallbackUrl = validateRedirectUrl(
    callbackUrl,
    ALLOWED_REDIRECT_ORIGINS,
  );

  const customer = await v2_fetchAuthorizedCustomer(app);

  if (customer) {
    return {
      redirect: {
        destination:
          validatedCallbackUrl ??
          routing.localized(
            app.locale,
            routing.shippingRoute({ shipping: 'courier' }),
          ),
        permanent: false,
      },
    };
  }

  const [providerMap, translationProps] = await Promise.all([
    getProviders(),
    getServerSideTranslationProps(app, 'messages'),
  ]);

  const providers = Object.keys(providerMap ?? {}).filter(
    (provider) => provider !== 'credentials',
  );

  return {
    props: {
      providers,
      ...translationProps,
    },
  };
};

export default LoginPage;
